import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useRef, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import GiftAnimation from './assets/Gift.json';
import BigNumber from 'bignumber.js';
import tether from './assets/tether.png';
import { useIsMobile } from '@/utils/CalVW';

interface RewardInfo {
  amount: string;
  currency?: string;
}

interface RewardAnimationProps {
  isVisible: boolean;
  rewardInfo: RewardInfo | null;
  onComplete?: () => void;
}

export const RewardAnimation = ({
  isVisible,
  rewardInfo,
  onComplete,
}: RewardAnimationProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [showReward, setShowReward] = useState(false);
  const [displayAmount, setDisplayAmount] = useState(0);

  const isMobile = useIsMobile();

  // 애니메이션 속도 설정
  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(0.5);
    }
  }, [isVisible]);

  // 보상 금액 카운트업 애니메이션
  useEffect(() => {
    if (showReward && rewardInfo) {
      const targetAmount = parseFloat(rewardInfo.amount);
      const duration = 1000; // 1초
      const steps = 30;
      const increment = targetAmount / steps;
      let current = 0;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          setDisplayAmount(targetAmount);
          clearInterval(timer);
        } else {
          setDisplayAmount(current);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [showReward, rewardInfo]);

  // 애니메이션 완료 후 호출되는 핸들러
  const handleAnimationComplete = () => {
    // 보물상자 애니메이션 완료 후 2초 더 보여준 뒤 숨김
    setTimeout(() => {
      onComplete?.();
      setShowReward(false);
      setDisplayAmount(0);
    }, 2000);
  };

  // Lottie 애니메이션 진행 중 보상 금액 표시
  useEffect(() => {
    if (isVisible) {
      // 0.8초 후 보상 금액 표시 (보물상자가 열릴 때쯤)
      const timer = setTimeout(() => {
        setShowReward(true);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      setShowReward(false);
      setDisplayAmount(0);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <AnimationOverlay onClick={onComplete}>
      <AnimationContainer $isMobile={isMobile}>
        {/* 보물상자 Lottie 애니메이션 */}
        <Lottie
          lottieRef={lottieRef}
          animationData={GiftAnimation}
          loop={false}
          autoplay={true}
          onComplete={handleAnimationComplete}
          style={{
            width: isMobile ? '200px' : '300px',
            height: isMobile ? '200px' : '300px',
          }}
        />

        {/* 보상 금액 표시 */}
        <AnimatePresence>
          {showReward && rewardInfo && (
            <motion.div
              initial={{ y: 0, opacity: 0, scale: 0.5 }}
              animate={{
                y: isMobile ? -60 : -120,
                opacity: 1,
                scale: 1,
              }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200,
              }}
              style={
                isMobile
                  ? {
                      // 모바일: 상대 위치로 상자 위에 배치
                      position: 'relative',
                      marginTop: '-180px',
                      marginBottom: '180px',
                    }
                  : {
                      // 데스크톱: 절대 위치로 중앙에 겹쳐서 배치
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }
              }
            >
              <RewardAmountContainer $isMobile={isMobile}>
                <GlowEffect />
                <RewardContent>
                  <RewardLabel $isMobile={isMobile}>Reward!</RewardLabel>
                  <RewardAmountWrapper>
                    <TetherIcon
                      src={tether}
                      alt="USDT"
                      $isMobile={isMobile}
                    />
                    <RewardAmount $isMobile={isMobile}>
                      {new BigNumber(displayAmount).toFormat(2)}
                    </RewardAmount>
                    <RewardCurrency $isMobile={isMobile}>
                      {rewardInfo.currency || 'USDT'}
                    </RewardCurrency>
                  </RewardAmountWrapper>
                </RewardContent>
              </RewardAmountContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </AnimationContainer>
    </AnimationOverlay>
  );
};

const AnimationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
`;

const AnimationContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding: ${(props) => (props.$isMobile ? '20px' : '0')};
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.6),
                0 0 40px rgba(255, 215, 0, 0.4),
                0 0 60px rgba(255, 215, 0, 0.2);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.8),
                0 0 60px rgba(255, 215, 0, 0.6),
                0 0 90px rgba(255, 215, 0, 0.4);
  }
`;

const RewardAmountContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 50%, #ffd700 100%);
  border-radius: ${(props) => (props.$isMobile ? '12px' : '20px')};
  padding: ${(props) => (props.$isMobile ? '16px 24px' : '24px 40px')};
  box-shadow: 0 10px 40px rgba(255, 215, 0, 0.5);
  animation: ${glow} 2s ease-in-out infinite;
  border: ${(props) => (props.$isMobile ? '2px' : '3px')} solid #fff;
  max-width: ${(props) => (props.$isMobile ? 'calc(100vw - 40px)' : 'none')};
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(
    circle,
    rgba(255, 215, 0, 0.4) 0%,
    rgba(255, 215, 0, 0) 70%
  );
  border-radius: 20px;
  pointer-events: none;
`;

const RewardContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const RewardLabel = styled.div<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? '14px' : '18px')};
  font-weight: 800;
  color: #8b4513;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const RewardAmountWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TetherIcon = styled.img<{ $isMobile: boolean }>`
  width: ${(props) => (props.$isMobile ? '24px' : '32px')};
  height: ${(props) => (props.$isMobile ? '24px' : '32px')};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
`;

const RewardAmount = styled.div<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? '24px' : '36px')};
  font-weight: 900;
  color: #2c1810;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-family: 'Pretendard', sans-serif;
  line-height: 1;
`;

const RewardCurrency = styled.div<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? '16px' : '24px')};
  font-weight: 700;
  color: #4a2c1a;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
`;
